/**
 * @fileoverview Constants for Click&Pledge API endpoints and configuration.
 * Contains all API URLs, keys, and other constants used throughout the application.
 */

import { isDevelopment } from '../../../../base/util/devUtils';


/** Check if current environment is development (dev server) */
const isDevelopmentApi = isDevelopment();

const CNP_APP_MODE = isDevelopmentApi ? 'BZ' : 'LIVE';

export let cnpAPIURL = '';
export let cnpAstURL = '';

if (CNP_APP_MODE === 'BZ') {
    cnpAstURL = 'https://stage2.br.clickandpledge.in/payquickly/';
    cnpAPIURL = 'https://api.us.clickandpledge.bz/';
} else if (CNP_APP_MODE === 'TEST') {
    cnpAstURL = 'https://test.payquickly.clickandpledge.com/';
    cnpAPIURL = 'https://test.api.cloud.clickandpledge.com/';
} else if (CNP_APP_MODE === 'LIVE') {
    cnpAstURL = 'https://payquickly.clickandpledge.com/';
    cnpAPIURL = 'https://api.cloud.clickandpledge.com/';
}


// Production (Live) API endpoints
const PROD_APIS = {
    AUTH_API: 'https://ryl3wy4jlj.execute-api.us-east-1.amazonaws.com/givent',
    GET_FORM_API: 'https://k0n2u364yj.execute-api.us-east-1.amazonaws.com/givent',
    GET_FORM_BY_PARAMS_API: 'https://teobg0rds1.execute-api.us-east-1.amazonaws.com/givent',
    FORM_SETTINGS_API: 'https://vwl35bql8c.execute-api.us-east-1.amazonaws.com/givent',
    SUBMIT_SOAP_API: 'https://u8u6qhnpwk.execute-api.us-east-1.amazonaws.com/givent'
};

// Development API endpoints
const DEV_APIS = {
    AUTH_API: 'https://o1gxr2k611.execute-api.us-east-1.amazonaws.com/zoomAPIs',
    GET_FORM_API: 'https://czgizz482a.execute-api.us-east-1.amazonaws.com/getformdetails',
    GET_FORM_BY_PARAMS_API: 'https://xi1cvoj4f5.execute-api.us-east-1.amazonaws.com/zoom',
    FORM_SETTINGS_API: 'https://rdiqsd9jy8.execute-api.us-east-1.amazonaws.com/zoom',
    SUBMIT_SOAP_API: 'https://hpbh657yp3.execute-api.us-east-1.amazonaws.com/submit'
};

// Select API endpoints based on environment
const APIS = isDevelopmentApi ? DEV_APIS : PROD_APIS;

/** API endpoint for authentication services */
export const AUTH_API = APIS.AUTH_API;

/** API endpoint for getting form details */
export const GET_FORM_API = APIS.GET_FORM_API;

/** API endpoint for getting forms by parameters */
export const GET_FORM_BY_PARAMS_API = APIS.GET_FORM_BY_PARAMS_API;

/** API endpoint for form settings */
export const FORM_SETTINGS_API = APIS.FORM_SETTINGS_API;

/** API endpoint for SOAP submission */
export const SUBMIT_SOAP_API = APIS.SUBMIT_SOAP_API;

/** Salt key for encryption */
export const SALT_KEY = 'gatheringjar$@0047';

/** Encryption passphrase for XML processing */
export const XML_ENCRYPTION_KEY = 'PQ@20204@PASS';

/** Gateway Configs */
export const GATEWAY_CONFIG = {
    BZ: {
        AMAZON_PAY_API: 'https://gcpunhdm3ez5vijzaqo7tl767u0zmzla.lambda-url.ap-south-1.on.aws/',
        STRIPE_INTENT_API: 'https://jln6j2e3qmlrm5ft52jawq2w3m0napgz.lambda-url.ap-south-1.on.aws/',
        PAYPAL_RECURRING_API: 'https://eohuh4jza63osfxxjr3hiylhqm0xqrmz.lambda-url.ap-south-1.on.aws/',
        PAYPAL_CREATE_ORDER_API: 'https://tewza6ovdsvekmpn2al3a4ht6e0farmb.lambda-url.ap-south-1.on.aws/',
        GATEWAY_PROCESS_API: 'https://uhtrfxxqmbiooaoy3jao7xfo6u0ratyv.lambda-url.ap-south-1.on.aws/',
        PAYPAL_CLIENT_ID: 'AbnZ3lA18tFW9CwySaLTk2SqYuYyG03r-fjlpCLwCtYhbKbuBFVdU3dXRFEqOw23KDeOz40sbdVqQN5B',
        STRIPE_PK: 'pk_test_51JkXsiH8mU0lDjNKzvzyu2nqrhY34ZCg6FOd2N2ZNgnO50YnMbKSQE69d2aCVzs8fQ48w0Ez81YCWUnp0ingT10T00ncFMd3PN' // Assuming Test Key for BZ
    },
    TEST: {
        AMAZON_PAY_API: 'https://vmnxhljymuqpxol7mpi3vlqx3u0opdjs.lambda-url.us-east-1.on.aws/',
        STRIPE_INTENT_API: 'https://guqqln2ybv7qyeaz4rn36ag2gu0bsfig.lambda-url.us-east-1.on.aws/',
        PAYPAL_RECURRING_API: 'https://opceqx2fgj3jnpnc45u3qiplna0mlzux.lambda-url.us-east-1.on.aws/',
        PAYPAL_CREATE_ORDER_API: 'https://l7nldhmiay5h6aqwze73wkmw3m0gyehl.lambda-url.us-east-1.on.aws/',
        GATEWAY_PROCESS_API: 'https://kj5hc2v3aj5ezqs77upgxzi5ym0ymtck.lambda-url.us-east-1.on.aws/',
        PAYPAL_CLIENT_ID: 'AbnZ3lA18tFW9CwySaLTk2SqYuYyG03r-fjlpCLwCtYhbKbuBFVdU3dXRFEqOw23KDeOz40sbdVqQN5B',
        STRIPE_PK: 'pk_test_51JkXsiH8mU0lDjNKzvzyu2nqrhY34ZCg6FOd2N2ZNgnO50YnMbKSQE69d2aCVzs8fQ48w0Ez81YCWUnp0ingT10T00ncFMd3PN'
    },
    LIVE: {
        AMAZON_PAY_API: 'https://hhmtuzwgufpnklmojwwqd6d35y0skdiz.lambda-url.us-east-1.on.aws/',
        STRIPE_INTENT_API: 'https://lfwufcxphleqas3ntentzyrmcm0dtrqr.lambda-url.us-east-1.on.aws/',
        PAYPAL_RECURRING_API: 'https://ycuf7z5pkdklvn5vwmzfr4c3gy0yamhy.lambda-url.us-east-1.on.aws/',
        PAYPAL_CREATE_ORDER_API: 'https://c4ouwl2raxwozn6vd6octjezpy0jnugp.lambda-url.us-east-1.on.aws/',
        GATEWAY_PROCESS_API: 'https://7gn5qm3ttlpwk5nf7ah53gjydq0bimds.lambda-url.us-east-1.on.aws/',
        PAYPAL_CLIENT_ID: 'AXypFpZuG-ImdbRHSu8vzrPfJefzn8hbs7ab1206h7QHPurCqBFskI5FrwpxFDcFgwCYoOcD7zFTesOL',
        STRIPE_PK: 'pk_live_51JkXsiH8mU0lDjNKfUaSOJqDjsE8Jo94ZWUEn4lx08s5M4j2mP5bv6JPqdeb24lpyQtceRT6xwuHv3Zgdc6uLiLk003auuaptz'
    }
};

/**
 * Get Gateway Configuration for specific mode
 * @param {string} mode - The environment mode (BZ, TEST, LIVE)
 * @returns {Object} Configuration object for that mode
 */
export const getGatewayConfig = mode => {
    const safeMode = (mode || 'LIVE').toUpperCase();

    if (GATEWAY_CONFIG[safeMode]) {
        return GATEWAY_CONFIG[safeMode];
    }

    return GATEWAY_CONFIG.LIVE;
};

/**
 * Current environment info for debugging
 */
export const ENVIRONMENT = {
    isDevelopmentApi,
    environment: isDevelopmentApi ? 'development' : 'production'
};
